import styled from 'styled-components';
import { useSearchContext } from '../context/SearchContext';
import { SearchFiltersActionTypes } from '../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import { updateQuery } from '../../../helpers/common';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import SearchLoader from './SearchLoader';
import { logEvent } from '../../../new-lib/analytics';
import { AreasApis } from '../../../new-lib/apis/areas.apis';
import { useQuery } from 'react-query';

const getAreasFilter = async (
  ids: number[]
): Promise<{ id: number; name: string }[]> => {
  if (!ids || ids.length === 0) return [];

  try {
    const fetchedAreas = await AreasApis.getSubAreas(ids);
    if (fetchedAreas.length > 0) {
      return fetchedAreas;
    } else {
      return AreasApis.getNeighboringAreas(ids);
    }
  } catch (error) {
    return [];
  }
};

const useAreasFilter = (ids: number[], locale?: string) => {
  return useQuery({
    queryKey: ['areasFilter', ids, locale], // Caching based on areas and locale
    queryFn: () => getAreasFilter(ids),
    staleTime: 10 * 60 * 1000, // Cache for 5 minutes
    enabled: ids?.length > 0 // Only run if areas exist
  });
};

const AreasFilter = () => {
  const router = useRouter();
  const { locale } = router;
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { searchFiltersDispatch, searchFiltersState } = useSearchContext();
  const isFetching = useSelector(
    (state: any) => state.searchResults.searchResults.isFetching
  );

  const { data: areas = [] } = useAreasFilter(
    searchFiltersState.searchFilters.areas,
    locale
  );

  const handleClick = async (id: number, name: string) => {
    if (
      !searchFiltersState.searchFilters[SearchFiltersTypes.AREAS].includes(id)
    ) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
        payload: { name: SearchFiltersTypes.AREAS, value: [id] }
      });
      updateQuery(
        router,
        [{ name: SearchFiltersTypes.AREAS, value: [id].toString() }],
        undefined
      );
    }
    logEvent.click('area_quick_filter_click', 'Search', 'Search', {
      area_id: searchFiltersState.searchFilters.areas[0],
      nearby_area_id: id,
      nearby_area_name: name
    });
  };
  return isFetching && !isMobile ? (
    <SearchLoader isFetching={isFetching} />
  ) : areas?.length > 0 ? (
    <Container className="hide-scrollbar">
      {areas?.map(area => (
        <div
          key={area.id}
          className="sub-area-filter "
          onClick={() => handleClick(area.id, area.name)}
        >
          <span className="area-name">{area.name}</span>
        </div>
      ))}
    </Container>
  ) : null;
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 16px;
  cursor: pointer;
  margin: 16px 0;

  .sub-area-filter {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    .area-name {
      font-size: 14px;
      color: #1e6dac;
      font-family: materialMedium;
      text-decoration: underline;
      white-space: nowrap;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 8px;
  }
`;

export default AreasFilter;
